import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useMemo, useState } from 'react'
import { fetchMgmtProfile } from '../lib/accountApi'
import {
  buildProfileFields,
  profileCompletion,
  type MgmtUserProfile,
} from '../lib/profile'
import { getAccountAccessToken } from '../lib/accessToken'
import { copy } from '../lib/i18n'
import {
  formatRelative,
  formatSessionDevice,
  isMobileSession,
  listSessions,
  type Auth0Session,
} from '../lib/sessions'
import { RangoAlert } from '../ui/RangoFeedback'
import './account.css'

const fmt = (template: string, vars: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (_, key) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  )

/**
 * S16 — Visão geral da conta: saudação, progresso do perfil e resumo da
 * sessão atual, tudo em pt-BR e nos tokens do Rango DS.
 */
export function OverviewPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const [mgmt, setMgmt] = useState<MgmtUserProfile | null>(null)
  const [sessions, setSessions] = useState<Auth0Session[]>([])
  const [sessionError, setSessionError] = useState<string>()

  const fields = useMemo(() => buildProfileFields(user, mgmt), [user, mgmt])
  const completion = useMemo(() => profileCompletion(fields), [fields])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccountAccessToken(getAccessTokenSilently)
        const [sess, prof] = await Promise.all([
          listSessions(token),
          fetchMgmtProfile(token),
        ])
        if (cancelled) return
        setSessions(sess.sessions.slice(0, 1))
        if (sess.error) setSessionError(sess.error)
        if (prof.profile) setMgmt(prof.profile)
      } catch {
        if (!cancelled) setSessionError(copy.security.loadError)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently])

  const name =
    fields.find((field) => field.key === 'name')?.value ||
    user?.name ||
    user?.nickname ||
    user?.email ||
    copy.overview.noName
  const current = sessions[0]

  return (
    <>
      <h1 className="acct-title">{fmt(copy.overview.greeting, { name })}</h1>
      <p className="acct-sub">{copy.overview.subtitle}</p>

      <div className="acct-grid">
        <section className="acct-card" aria-labelledby="acct-completion-title">
          <div className="acct-card-body">
            <div className="acct-progress">
              <div className="acct-shield" aria-hidden="true">
                <span className="acct-shield-check">✓</span>
              </div>
              <div className="acct-progress-main">
                <h2 className="acct-card-title" id="acct-completion-title">
                  {copy.overview.completionTitle}
                </h2>
                <p className="acct-card-desc">
                  {fmt(copy.overview.completionDesc, {
                    percent: completion.percent,
                  })}
                </p>
                {/* Div em vez de <progress>: Safari não estiliza o nativo. */}
                <div
                  className="acct-progress-bar"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={completion.percent}
                  aria-label={fmt(copy.overview.completionDesc, {
                    percent: completion.percent,
                  })}
                >
                  <span style={{ width: `${completion.percent}%` }} />
                </div>
                <ul className="acct-dots">
                  <li className="ok">
                    {fmt(copy.overview.mandatoryDone, {
                      done: completion.mandatoryDone,
                      total: completion.mandatoryTotal,
                    })}
                  </li>
                  <li className="warn">
                    {fmt(copy.overview.optionalDone, {
                      done: completion.optionalDone,
                      total: completion.optionalTotal,
                    })}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="acct-card-foot">
            <Link className="acct-link" to="/account/personal-info">
              {copy.overview.profileLink}
            </Link>
          </div>
        </section>

        <section className="acct-card" aria-labelledby="acct-sessions-title">
          <div className="acct-card-body">
            <h2 className="acct-card-title" id="acct-sessions-title">
              {copy.overview.sessionsTitle}
            </h2>
            <p className="acct-card-desc">{copy.overview.sessionsDesc}</p>
            {sessionError ? (
              <div className="acct-overview-alert">
                <RangoAlert tone="warning">{sessionError}</RangoAlert>
              </div>
            ) : null}
            {current ? (
              <div className="acct-session">
                <div
                  className={`acct-session-icon ${isMobileSession(current) ? 'mobile' : 'desktop'}`}
                  aria-hidden="true"
                />
                <div className="acct-session-meta">
                  <strong>{formatSessionDevice(current)}</strong>
                  <span>
                    {copy.security.lastAccess.replace(
                      '{when}',
                      formatRelative(current.updated_at || current.authenticated_at),
                    )}
                  </span>
                </div>
              </div>
            ) : !sessionError ? (
              <div className="acct-session">
                <div className="acct-session-icon desktop" aria-hidden="true" />
                <div className="acct-session-meta">
                  <strong>{copy.overview.sessionFallback}</strong>
                  <span>{copy.overview.sessionFallbackDesc}</span>
                </div>
              </div>
            ) : null}
          </div>
          <div className="acct-card-foot">
            <Link className="acct-link" to="/account/security">
              {copy.overview.manageSessions}
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
